import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface PaginationProps {
  page: number;
  totalPages: number;
  total?: number;
  pageSize?: number;
  onChange: (page: number) => void;
  accent?: "indigo" | "fuchsia";
  className?: string;
}

export function Pagination({
  page,
  totalPages,
  total,
  pageSize,
  onChange,
  accent = "indigo",
  className,
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const showRange = typeof total === "number" && typeof pageSize === "number";
  const start = showRange ? (page - 1) * pageSize! + 1 : 0;
  const end = showRange ? Math.min(page * pageSize!, total!) : 0;

  return (
    <div
      className={cn("flex items-center justify-between border-t border-gray-100 pt-4", className)}
    >
      {showRange ? (
        <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
          {start}–{end} sur {total}
        </span>
      ) : (
        <span />
      )}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-50 disabled:opacity-30"
        >
          <ChevronLeft size={16} />
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            className={cn(
              "h-8 w-8 rounded-lg text-[10px] font-black transition-all",
              p === page
                ? accent === "fuchsia"
                  ? "bg-fuchsia-600 text-white shadow-sm"
                  : "bg-indigo-600 text-white shadow-sm"
                : "text-gray-400 hover:bg-gray-50",
            )}
          >
            {p}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
          className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-50 disabled:opacity-30"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
